import React, { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Card, Button, Spin, Alert, Typography, Divider, Result } from 'antd';
import {
  CreditCardOutlined,
  CheckCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { createUseStyles } from 'react-jss';
import { loadStripe } from '@stripe/stripe-js';
import { theme } from '../constants/theme';

const { Title, Text } = Typography;

const useStyles = createUseStyles({
  container: {
    minHeight: '100vh',
    backgroundColor: '#f8f9fa',
    padding: '40px 20px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  card: {
    maxWidth: '600px',
    width: '100%',
    borderRadius: '16px',
    boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
    '& .ant-card-body': {
      padding: '48px',
    },
  },
  header: {
    textAlign: 'center',
    marginBottom: '32px',
  },
  logo: {
    fontSize: '48px',
    color: theme.palette.primary,
    marginBottom: '16px',
  },
  title: {
    fontFamily: "'Baloo 2'",
    fontWeight: '600',
    fontSize: '28px !important',
    color: '#1f2937',
    marginBottom: '8px !important',
  },
  subtitle: {
    fontSize: '16px',
    color: '#6b7280',
    marginBottom: '0 !important',
  },
  projectInfo: {
    backgroundColor: '#f3f4f6',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '32px',
  },
  projectTitle: {
    fontWeight: '600',
    fontSize: '18px',
    color: '#1f2937',
    marginBottom: '8px !important',
  },
  projectDetail: {
    color: '#6b7280',
    fontSize: '14px',
    marginBottom: '4px',
  },
  paymentButton: {
    width: '100%',
    height: '56px',
    backgroundColor: theme.palette.primary,
    borderColor: theme.palette.primary,
    fontSize: '18px',
    fontWeight: '600',
    borderRadius: '8px',
    '&:hover': {
      backgroundColor: '#3730a3',
      borderColor: '#3730a3',
    },
  },
  securityInfo: {
    marginTop: '24px',
    backgroundColor: '#fef3cd',
    border: '1px solid #fde68a',
    borderRadius: '8px',
    padding: '16px',
  },
  loadingSpinner: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '200px',
    fontSize: '18px',
  },
  errorContainer: {
    textAlign: 'center',
    padding: '40px 20px',
  },
  messageBox: {
    backgroundColor: '#f8f9ff',
    border: '1px solid #e0e7ff',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '24px',
  },
  payerInfo: {
    backgroundColor: '#f0f9ff',
    border: '1px solid #bfdbfe',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '24px',
  },
  dividerText: {
    color: '#6b7280',
    fontSize: '14px',
  },
});

interface PaymentLinkData {
  userId: string;
  projectId: string;
  type: 'ngo' | 'donor';
  offer: string;
  isMonthly: string;
  pricing: string;
  firstName: string;
  lastName: string;
  email: string;
  projectName?: string;
  message?: string;
  createdAt?: string;
}

export const PaymentLink: React.FC = () => {
  const classes = useStyles();
  const { token } = useParams<{ token: string }>();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [paymentData, setPaymentData] = useState<PaymentLinkData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [processingPayment, setProcessingPayment] = useState(false);

  useEffect(() => {
    if (token) {
      fetchPaymentLinkData();
    }
  }, [token]);

  const fetchPaymentLinkData = async () => {
    try {
      setLoading(true);
      const API_URL = process.env.REACT_APP_API_URL;
      const response = await fetch(`${API_URL}/payment/link/${token}`);

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Ce lien de paiement est invalide ou a expiré.');
        }
        const errorData = await response.json();
        throw new Error(
          errorData.message ||
            'Impossible de charger les informations de paiement.',
        );
      }

      const data = await response.json();
      setPaymentData(data);
    } catch (error) {
      console.error('Error fetching payment link data:', error);
      setError(
        error instanceof Error
          ? error.message
          : 'Impossible de charger les informations de paiement.',
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!paymentData) return;

    try {
      setProcessingPayment(true);

      const API_URL = process.env.REACT_APP_API_URL;
      const response = await fetch(`${API_URL}/payment/linkCheckout/${token}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          originUrl: window.location.origin,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || 'Impossible de créer la session de paiement.',
        );
      }

      const { sessionId } = await response.json();

      // Redirect to Stripe Checkout using the same pattern as existing payment system
      const stripe = await loadStripe(
        process.env.REACT_APP_STRIPE_PUBLIC_KEY ||
          'cannot retrieve REACT_APP_STRIPE_PUBLIC_KEY',
      );

      if (!stripe) {
        throw new Error('Impossible de charger Stripe. Veuillez réessayer.');
      }

      const { error: stripeError } = await stripe.redirectToCheckout({
        sessionId,
      });

      if (stripeError) {
        throw new Error(stripeError.message);
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      setError(
        error instanceof Error
          ? error.message
          : 'Impossible de traiter le paiement.',
      );
    } finally {
      setProcessingPayment(false);
    }
  };

  const getOfferName = (offer: string) => {
    return offer === '1' ? 'Premium' : 'Visibility';
  };

  const getPricingName = (type: string) => {
    // Simplified pricing system - all payment links use base tier
    if (type === 'ngo') {
      return 'Premium';
    } else {
      return 'Forfait Donateur';
    }
  };

  const getPrice = (
    type: string,
    offer: string,
    pricing: string,
    isMonthly: string,
  ) => {
    if (type === 'ngo' && offer === '1') {
      // Premium NGO pricing
      if (isMonthly === 'true') {
        return '9,90€ / mois';
      } else {
        return '374€ / an (économisez 20%)';
      }
    }
    return 'Contactez-nous pour le tarif';
  };

  if (loading) {
    return (
      <div className={classes.container}>
        <div className={classes.loadingSpinner}>
          <Spin
            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
            size="large"
          />
          <Text style={{ marginLeft: '16px' }}>
            Chargement des informations de paiement...
          </Text>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={classes.container}>
        <Card className={classes.card}>
          <Result
            status="error"
            title="Erreur du lien de paiement"
            subTitle={error}
            extra={[
              <Button
                key="home"
                type="primary"
                onClick={() => history.push('/')}
              >
                Aller à la page d&apos;accueil Sonar
              </Button>,
            ]}
          />
        </Card>
      </div>
    );
  }

  if (!paymentData) {
    return (
      <div className={classes.container}>
        <Card className={classes.card}>
          <Result
            status="404"
            title="Lien de paiement introuvable"
            subTitle="Le lien de paiement que vous recherchez n'existe pas ou a expiré."
            extra={[
              <Button
                key="home"
                type="primary"
                onClick={() => history.push('/')}
              >
                Aller à la page d&apos;accueil Sonar
              </Button>,
            ]}
          />
        </Card>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <Card className={classes.card}>
        <div className={classes.header}>
          <div className={classes.logo}>
            <CreditCardOutlined />
          </div>
          <Title level={2} className={classes.title}>
            Demande de paiement
          </Title>
          <Text className={classes.subtitle}>
            Vous avez été invité à payer un abonnement premium Sonar
          </Text>
        </div>

        <div className={classes.projectInfo}>
          <Title level={4} className={classes.projectTitle}>
            Projet : {paymentData.projectName}
          </Title>
          <div className={classes.projectDetail}>
            <strong>Forfait :</strong> {getOfferName(paymentData.offer)} -{' '}
            {getPricingName(paymentData.type)}
          </div>
          <div className={classes.projectDetail}>
            <strong>Prix :</strong>{' '}
            {getPrice(
              paymentData.type,
              paymentData.offer,
              paymentData.pricing,
              paymentData.isMonthly,
            )}
          </div>
          <div className={classes.projectDetail}>
            <strong>Facturation :</strong>{' '}
            {paymentData.isMonthly === 'true' ? 'Mensuelle' : 'Annuelle'}
          </div>
        </div>

        <div className={classes.payerInfo}>
          <Title level={5} style={{ marginBottom: '8px', color: '#1e40af' }}>
            Détails du paiement
          </Title>
          <div className={classes.projectDetail}>
            <strong>Paiement pour :</strong> {paymentData.firstName}{' '}
            {paymentData.lastName}
          </div>
          <div className={classes.projectDetail}>
            <strong>Email :</strong> {paymentData.email}
          </div>
        </div>

        {paymentData.message && (
          <div className={classes.messageBox}>
            <Title level={5} style={{ marginBottom: '8px', color: '#4338ca' }}>
              Message personnel
            </Title>
            <Text>{paymentData.message}</Text>
          </div>
        )}

        <Divider>
          <span className={classes.dividerText}>Paiement sécurisé</span>
        </Divider>

        <Button
          type="primary"
          size="large"
          icon={<CreditCardOutlined />}
          onClick={handlePayment}
          loading={processingPayment}
          className={classes.paymentButton}
        >
          {processingPayment
            ? 'Traitement en cours...'
            : 'Payer en sécurité avec Stripe'}
        </Button>

        <Alert
          message="Paiement sécurisé"
          description="Votre paiement est traité en toute sécurité par Stripe. Nous ne stockons jamais vos informations de carte bancaire."
          type="info"
          showIcon
          icon={<CheckCircleOutlined />}
          className={classes.securityInfo}
        />

        <div style={{ textAlign: 'center', marginTop: '24px' }}>
          <Text style={{ color: '#9ca3af', fontSize: '12px' }}>
            Propulsé par Sonar • Paiements sécurisés par Stripe
          </Text>
        </div>
      </Card>
    </div>
  );
};
